import {
  NOT_STATED, TERM_FIELDS, emptyManualAuthority, isNotStated, normalizeEffectiveDate,
  termDraft, termValue,
  type ManualAuthorityDraft, type TermField, type TermKind,
} from '../ManualAuthorityEditor';
import { businessLabel } from '../../presentation/contractPresentation';
import { emptyCollectionsSchedules } from '../../components/CollectionsSchedulesEditor';
import { emptyContractSchedules } from '../../components/ContractSchedulesEditor';

/**
 * The UI lane's entry model (FR-1.13, review standard 2026-09-11).
 *
 * Every contract value is asked for **once**, with the clause it comes from
 * beside it, and is written to both the facility record and the cited contract
 * term. The old form asked for the same commitment twice — once as reference
 * data and again as a term to verify — which is how a number and its own
 * provenance came to disagree.
 *
 * The rail's per-section problems are a decomposition of `validateManualAuthority`,
 * which remains the single gate at save: the page never claims a section is
 * complete on rules the save does not apply.
 */

/** How the firm refers to the facility. Not contract authority, so no clause. */
export interface IdentityDraft {
  facilityName: string;
  facilityCode: string;
  externalId: string;
}

export interface EntryDraft {
  identity: IdentityDraft;
  authority: ManualAuthorityDraft;
}

export const emptyEntry = (): EntryDraft => ({
  identity: { facilityName: '', facilityCode: '', externalId: '' },
  authority: {
    ...emptyManualAuthority(),
    collections: emptyCollectionsSchedules(),
    schedules: emptyContractSchedules(),
  },
});

/** A contract value the maker states, cited to its clause. */
export interface EntryField {
  key: string;
  kind: TermKind;
  options?: string[];
  hint?: string;
  /** Activation needs a value; it can never be recorded as not stated. */
  required?: boolean;
  /**
   * Economically material: the contract is not complete until someone answers
   * it, but the answer may be "the agreement does not state this". Mirrors
   * `economically_material` on the server's term catalog — the completeness rule
   * the save is refused by.
   */
  material?: boolean;
}

/**
 * Terms the server counts toward completeness (required_for_activation OR
 * economically_material in `ABFFacilityTwinService.TERMS`). A material term left
 * UNKNOWN keeps the version INCOMPLETE and the save is refused, so the page asks
 * for each one and offers an explicit not-stated decision where that is allowed.
 */
const REQUIRED_TERMS = new Set([
  'facilityName', 'obligorName', 'collateralType', 'abfStructure', 'currency',
  'totalCommitment', 'advanceRatePct', 'financingIndex', 'spreadBps',
  'effectiveDate', 'maturityDate',
]);
// Must be answered, may be recorded as not stated. Most agreements set no fixed reserve amount;
// a reserve the agreement does not state is no reserve.
const MATERIAL_TERMS = new Set([
  'drawnAmount', 'allInRatePct', 'rateFloorPct', 'unusedFeeBps', 'dayCount', 'closingDate', 'reserveAmount',
]);

const termField = (key: string): TermField | undefined => TERM_FIELDS.find(f => f.key === key);

const field = (key: string, _legacyRequired?: boolean, hint?: string): EntryField => {
  const source = termField(key);
  if (!source) throw new Error(`Unknown contract term: ${key}`);
  return {
    key, kind: source.kind, options: source.options, hint: hint || source.hint,
    required: REQUIRED_TERMS.has(key), material: MATERIAL_TERMS.has(key),
  };
};

export { NOT_STATED, isNotStated };

export type SectionKind = 'IDENTITY' | 'AUTHORITY' | 'TERMS' | 'RULES' | 'COVENANTS'
  | 'COLLECTIONS' | 'SCHEDULES';

export interface EntrySectionDef {
  key: string;
  kind: SectionKind;
  label: string;
  category: string;
  /** What the section is for, in the operator's words. */
  purpose: string;
  /** Where in an executed agreement these values are usually found. */
  whereFound: string;
  fields?: EntryField[];
}

export const ENTRY_SECTIONS: EntrySectionDef[] = [
  {
    key: 'identity', kind: 'IDENTITY', label: 'Name and references', category: 'The facility',
    purpose: 'How your firm refers to this facility. These are your references, not contract terms, so they carry no clause.',
    whereFound: 'Your own naming convention, plus any lender or system-of-record identifier you need to match on.',
  },
  {
    key: 'authority', kind: 'AUTHORITY', label: 'The agreement itself', category: 'The facility',
    purpose: 'Which executed document you are entering, the clause that governs the facility terms generally, and the date the contract takes effect.',
    whereFound: 'The cover page and the preamble: the agreement name and date, and the article that sets out the facility terms.',
  },
  {
    key: 'parties', kind: 'TERMS', label: 'Parties', category: 'Contract terms',
    purpose: 'Who is on the other side and who performs each role. A servicer or backup servicer named here is the one the platform will hold reports against.',
    whereFound: 'The preamble and the definitions article; servicing roles usually sit in the servicing article.',
    fields: [
      field('obligorName', true), field('sponsorName'), field('originatorName'),
      field('sellerName'), field('servicerName'), field('backupServicerName'),
      field('adminAgentName'),
    ],
  },
  {
    key: 'structure', kind: 'TERMS', label: 'Structure and collateral', category: 'Contract terms',
    purpose: 'What the facility is and what secures it. The collateral class drives which eligibility fields the tape must carry.',
    whereFound: 'The definitions article — "Eligible Receivable", "Revolving Period" — and the granting clause.',
    fields: [field('collateralType', true), field('abfStructure', true), field('currency', true)],
  },
  {
    key: 'capacity', kind: 'TERMS', label: 'Capacity and support', category: 'Contract terms',
    purpose: 'What the lenders commit, what is advanced at closing, and the contractual advance rate and reserve. The amount drawn at closing becomes a funding proposal, never a booked position.',
    whereFound: 'The commitment article, the borrowing-base definition, and the reserve account definition.',
    fields: [
      field('totalCommitment', true),
      field('drawnAmount', false, 'Advanced at closing. Recorded as a contract term and staged as a funding proposal at activation — never booked from here.'),
      field('advanceRatePct', true), field('reserveAmount', true),
    ],
  },
  {
    key: 'pricing', kind: 'TERMS', label: 'Pricing', category: 'Contract terms',
    purpose: 'What the facility costs. Enter the floor only if the agreement states one — a floor left blank is recorded as unanswered, never as "no floor".',
    whereFound: 'The interest article and the fee letter or fee article.',
    fields: [
      field('financingIndex', true), field('spreadBps', true), field('rateFloorPct'),
      field('allInRatePct'), field('unusedFeeBps'), field('dayCount'),
      field('tickingFeeBps', false, 'Delayed-draw facilities only: the fee on commitment not yet drawn during ramp-up'),
    ],
  },
  {
    key: 'structure-terms', kind: 'TERMS', label: 'Facility structure', category: 'Contract terms',
    purpose: 'Whether the commitment revolves, is drawn once over a delayed-draw period, or is a term loan; whether repaid amounts may be redrawn; and any term-out option. These decide the facility phase, the draw gate and when the unused fee stops.',
    whereFound: 'The commitment and borrowing articles, the definition of the Revolving or Availability Period, and any term-out or conversion clause.',
    fields: [
      field('commitmentType', false, 'Revolving unless the agreement says otherwise'),
      field('reborrowAllowed', false, 'May repaid amounts be redrawn? Revolving facilities default to yes'),
      field('termOutOption', false, 'The term-out option as written, if the agreement grants one'),
    ],
  },
  {
    key: 'dates', kind: 'TERMS', label: 'Dates', category: 'Contract terms',
    purpose: 'Closing, maturity, and when the first borrowing-base certificate is due. Activation generates the collection periods from these.',
    whereFound: 'The closing conditions article, the definition of the maturity or commitment termination date, and the reporting covenant.',
    fields: [
      field('closingDate'), field('maturityDate', true),
      field('commitmentTerminationDate', false, 'End of the revolving or reinvestment period, if the agreement states one'),
      field('amortizationStartDate', false, 'Amortization period commencement; the day after the termination date unless the agreement states one'),
      field('nextBorrowingBaseDate', false, 'First borrowing-base certificate'),
    ],
  },
  {
    key: 'rules', kind: 'RULES', label: 'Eligibility rules', category: 'What the contract enforces',
    purpose: 'The tests an asset must pass to count in the borrowing base. At least one row-level rule is required: without it the platform cannot compute a base at all.',
    whereFound: 'The "Eligible Receivable" definition and the concentration-limit schedule.',
  },
  {
    key: 'covenants', kind: 'COVENANTS', label: 'Financial covenants', category: 'What the contract enforces',
    purpose: 'The ratios tested each period, with the threshold and the formula the platform will compute. If the agreement has none, say so explicitly — silence is not an answer.',
    whereFound: 'The financial-covenant article and the compliance-certificate exhibit.',
  },
  {
    key: 'collections', kind: 'COLLECTIONS', label: 'Accounts and payment calendar', category: 'How cash moves',
    purpose: 'The accounts cash passes through and the monthly determination and payment days. Activation makes these live and schedules the first periods.',
    whereFound: 'The accounts article, the paying-agent letter, and the definition of the determination and payment dates.',
  },
  {
    key: 'schedules', kind: 'SCHEDULES', label: 'Waterfall, classes and consequences', category: 'How cash moves',
    purpose: 'The priority of payments, the lender tranches, and what the agreement does when a test fails. Only the payment types and consequences the engines actually honour are offered.',
    whereFound: 'The priority-of-payments article, the commitment schedule, and the amortisation or trigger-event article.',
  },
];

export type SectionStatus = 'OPEN' | 'DONE' | 'OPTIONAL';

export interface EntrySectionState {
  def: EntrySectionDef;
  status: SectionStatus;
  /** Why it is open, in one line the operator can act on. */
  problem: string | null;
  /** What has been entered so far. */
  summary: string;
}

const entered = (authority: ManualAuthorityDraft, key: string) => {
  const value = termDraft(authority, key).value;
  return value.trim() !== '' && !isNotStated(value);
};

const fieldProblem = (authority: ManualAuthorityDraft, entry: EntryField): string | null => {
  const draft = termDraft(authority, entry.key);
  const label = businessLabel(entry.key);
  if (isNotStated(draft.value)) return null;
  if (draft.value.trim() === '') {
    if (draft.sourceClause.trim()) return `${label} has a clause but no value`;
    if (entry.required) return `${label} is required`;
    // The contract is not complete while a material term is unanswered, but the
    // answer is allowed to be "the agreement does not state this".
    if (entry.material) return `${label} needs a value, or mark it not stated`;
    return null;
  }
  const parsed = termValue({ key: entry.key, kind: entry.kind, options: entry.options }, draft.value);
  return 'problem' in parsed ? parsed.problem : null;
};

/** The state of every section, in rail order. */
export const buildEntrySections = (draft: EntryDraft): EntrySectionState[] =>
  ENTRY_SECTIONS.map(def => {
    const { identity, authority } = draft;
    let problem: string | null = null;
    let summary = '';
    let optional = false;

    if (def.kind === 'IDENTITY') {
      if (!identity.facilityName.trim()) problem = 'A facility name is required';
      const extras = [identity.facilityCode.trim() && 'code', identity.externalId.trim() && 'external reference']
        .filter(Boolean);
      summary = identity.facilityName.trim()
        ? `${identity.facilityName.trim()}${extras.length ? ` · ${extras.join(', ')} entered` : ''}`
        : 'Not named';
    } else if (def.kind === 'AUTHORITY') {
      if (!authority.authorityReference.trim()) problem = 'Name the executed agreement you are entering';
      else if (!authority.termSourceClause.trim()) problem = 'Give the clause that governs the facility terms';
      else if (!normalizeEffectiveDate(authority.effectiveFrom)) problem = 'Enter a valid contract effective date';
      summary = authority.authorityReference.trim()
        ? `${authority.authorityReference.trim()}${normalizeEffectiveDate(authority.effectiveFrom) ? ` · effective ${normalizeEffectiveDate(authority.effectiveFrom)}` : ''}`
        : 'No agreement named';
    } else if (def.kind === 'TERMS') {
      const fields = def.fields || [];
      problem = fields.map(entry => fieldProblem(authority, entry)).find(Boolean) || null;
      const count = fields.filter(entry => entered(authority, entry.key)).length;
      const silent = fields.filter(entry => isNotStated(termDraft(authority, entry.key).value)).length;
      summary = count || silent
        ? `${count} of ${fields.length} entered${silent ? ` · ${silent} not stated` : ''}`
        : 'None entered';
      optional = !problem && count === 0 && silent === 0;
    } else if (def.kind === 'RULES') {
      const rules = authority.eligibilityRules;
      const complete = rules.filter(rule => rule.ruleName.trim() && rule.fieldName.trim()
        && (rule.thresholdValue !== '' || rule.thresholdText.trim()) && rule.sourceClause.trim());
      if (!complete.length) problem = 'At least one complete eligibility rule is required';
      else if (!complete.some(rule => ['ROW', 'ROW_OPTIONAL'].includes(rule.ruleType))) {
        problem = 'At least one row-level rule is required';
      } else if (complete.length !== rules.length) problem = `${rules.length - complete.length} rule(s) incomplete`;
      summary = complete.length ? `${complete.length} rule${complete.length === 1 ? '' : 's'} stated` : 'None stated';
    } else if (def.kind === 'COVENANTS') {
      if (authority.noFinancialCovenants) summary = 'Attested: the agreement has none';
      else {
        const covenants = authority.covenants;
        const complete = covenants.filter(covenant => covenant.covenantKey.trim() && covenant.covenantName.trim()
          && covenant.thresholdValue !== '' && covenant.formulaExpression && covenant.sourceClause.trim());
        if (!complete.length) problem = 'Enter the covenants, or attest that the agreement has none';
        else if (complete.length !== covenants.length) problem = `${covenants.length - complete.length} covenant(s) incomplete`;
        summary = complete.length ? `${complete.length} covenant${complete.length === 1 ? '' : 's'} stated` : 'None stated';
      }
    } else if (def.kind === 'COLLECTIONS') {
      const collections = draft.authority.collections || emptyCollectionsSchedules();
      const accounts = collections.accounts.filter(account => account.name.trim());
      const calendar = collections.calendar;
      const hasCalendar = Boolean(calendar && calendar.determinationDay && calendar.paymentDay);
      summary = `${accounts.length} account${accounts.length === 1 ? '' : 's'}${hasCalendar ? ' · calendar entered' : ''}`;
      optional = accounts.length === 0 && !hasCalendar;
      if (optional) summary = 'Not entered';
    } else {
      const schedules = draft.authority.schedules || emptyContractSchedules();
      const nodes = schedules.waterfallNodes.filter(node => node.nodeName.trim());
      const classes = schedules.lenderClasses.filter(lenderClass => lenderClass.className.trim());
      const consequences = schedules.consequences.filter(consequence => consequence.consequenceKey.trim());
      const parts = [
        nodes.length && `${nodes.length} waterfall step${nodes.length === 1 ? '' : 's'}`,
        classes.length && `${classes.length} class${classes.length === 1 ? '' : 'es'}`,
        consequences.length && `${consequences.length} consequence${consequences.length === 1 ? '' : 's'}`,
      ].filter(Boolean);
      summary = parts.length ? parts.join(' · ') : 'Not entered';
      optional = parts.length === 0;
    }

    return { def, status: problem ? 'OPEN' : optional ? 'OPTIONAL' : 'DONE', problem, summary };
  });

/**
 * The facility record the create endpoint needs, derived from what was entered
 * once. A contract value is never typed twice, so the record and the cited term
 * cannot disagree.
 */
export const entryRecordPayload = (draft: EntryDraft): Record<string, unknown> => {
  const { identity, authority } = draft;
  const value = (key: string): unknown => {
    const entry = TERM_FIELDS.find(f => f.key === key);
    const raw = termDraft(authority, key).value;
    if (!entry || raw.trim() === '' || isNotStated(raw)) return null;
    const parsed = termValue(entry, raw.trim());
    return 'problem' in parsed ? null : parsed.value;
  };
  return {
    facilityName: identity.facilityName.trim() || value('facilityName'),
    facilityCode: identity.facilityCode.trim() || null,
    externalId: identity.externalId.trim() || null,
    obligorName: value('obligorName'),
    sponsorName: value('sponsorName'),
    originatorName: value('originatorName'),
    sellerName: value('sellerName'),
    servicerName: value('servicerName'),
    backupServicerName: value('backupServicerName'),
    adminAgentName: value('adminAgentName'),
    collateralType: value('collateralType'),
    abfStructure: value('abfStructure'),
    currency: value('currency'),
    totalCommitment: value('totalCommitment'),
    // Never on the record: a manual reference may not assert funded exposure
    // (`createManualReference` refuses it). The amount drawn at closing travels
    // as a contract term, and activation stages it as a closing-advance proposal
    // a checker approves — which is the only way funded exposure is ever booked.
    drawnAmount: null,
    advanceRatePct: value('advanceRatePct'),
    reserveAmount: value('reserveAmount'),
    financingIndex: value('financingIndex'),
    spreadBps: value('spreadBps'),
    unusedFeeBps: value('unusedFeeBps'),
    closingDate: value('closingDate'),
    maturityDate: value('maturityDate'),
    // The contract effective date is asked once, on the agreement itself.
    effectiveDate: normalizeEffectiveDate(authority.effectiveFrom),
  };
};

/** Term keys the maker recorded the agreement as silent on. */
export const notStatedKeys = (draft: EntryDraft): string[] => ENTRY_SECTIONS
  .flatMap(section => section.fields || [])
  .filter(entry => isNotStated(termDraft(draft.authority, entry.key).value))
  .map(entry => entry.key);

/** Contract values the operator must enter before the record can be created. */
export const REQUIRED_KEYS = ENTRY_SECTIONS
  .flatMap(section => section.fields || [])
  .filter(entry => entry.required)
  .map(entry => entry.key);
